/**
 * EmployeeRosterCard — one tile in the searchable /agents roster grid
 * (DIGEMP-EMPLOYEE-ROSTER-WORKSPACE-002). Distinct from EmployeeIdentityCard, which is the
 * Growth Profile / CV section inside the employee workspace (DIGEMP-GROWTH-PROFILE-FRONTEND-023)
 * — the two were built independently and happened to share a name; this one owns the roster's
 * identity/status/capability tile and the per-employee enable toggle, not the CV.
 */
import { Link } from "react-router-dom";
import EmployeeAvatar from "./EmployeeAvatar";
import { isUnrestrictedAllowlist } from "../utils/toolAllowlist";
import { FOCUS_RING_CLS, TOUCH_TARGET_CLS } from "./designSystem/designTokens";

const TIER_BADGE = {
  tier_1: { text: "T1", cls: "bg-green-100 text-green-700" },
  tier_2: { text: "T2", cls: "bg-amber-100 text-amber-700" },
  tier_3: { text: "T3", cls: "bg-red-100 text-red-700" },
};

// The role line names what the employee is allowed to do (permission tier); the
// autonomy chip names how much runs without a human. Both are real policy fields.
const ROLE_LABEL = { tier_1: "עובד קריאה", tier_2: "עובד תפעולי", tier_3: "עובד מוגבה" };

const AUTONOMY_LABEL = {
  approval_required: "אישור נדרש לכל פעולה",
  supervised: "מפוקח — כתיבה באישור",
  semi_autonomous: "חצי אוטונומי",
  autonomous: "אוטונומי",
};

const MAX_TOOL_CHIPS = 3;

const formatDateTime = (value) =>
  value ? new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(value)) : "—";

const scoreColor = (score) => (score >= 70 ? "#16a34a" : score >= 40 ? "#d97706" : "#dc2626");

export default function EmployeeRosterCard({ agent, score, canManage = false, toggling = false, onToggle }) {
  const tier = TIER_BADGE[agent.permission_tier] || TIER_BADGE.tier_1;
  const role = ROLE_LABEL[agent.permission_tier] || "עובד";
  const autonomy = AUTONOMY_LABEL[agent.autonomy_level];
  const tools = Array.isArray(agent.allowed_tools) ? agent.allowed_tools : [];
  // NULL/empty allowed_tools means unrestricted-for-tier on the backend, not "no tools".
  const unrestricted = isUnrestrictedAllowlist(agent.allowed_tools);

  const pendingCount = agent.pending_runs_count || 0;
  // Availability synthesizes the policy state with the approval queue, so "can this
  // employee work right now, and is anything waiting on a human?" reads in one line.
  const availability = !agent.is_enabled
    ? { text: "לא זמין — מושבת", cls: "bg-gray-50 border-gray-200 text-gray-500", dotCls: "bg-gray-300" }
    : pendingCount > 0
      ? { text: "זמין — הרצות ממתינות לאישור", cls: "bg-orange-50 border-orange-200 text-orange-700", dotCls: "bg-orange-400" }
      : { text: "זמין לעבודה", cls: "bg-green-50 border-green-200 text-green-700", dotCls: "bg-green-500" };

  return (
    <article aria-label={agent.name} className="bg-white rounded-xl border shadow-sm p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <EmployeeAvatar
          name={agent.name}
          avatarKey={agent.avatar_key}
          avatarColor={agent.avatar_color}
          status={agent.availability_status}
          isEnabled={agent.is_enabled}
          size="lg"
          showStatus
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <Link to={`/agents/${agent.id}`} className="font-semibold text-brand-600 hover:underline truncate">
              {agent.name}
            </Link>
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${
                agent.is_enabled ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-500 border-gray-200"
              }`}
              title={agent.is_enabled ? "העובד פעיל ויכול לקבל הרצות" : "העובד מושבת — הרצות חדשות לא יתבצעו"}
            >
              {agent.is_enabled ? "פעיל" : "מושבת"}
            </span>
          </div>
          {agent.role_title ? (
            <p className="text-xs font-medium text-violet-700 mt-0.5 truncate">{agent.role_title}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{agent.description || "ללא תיאור תפקיד"}</p>
          )}
          {agent.client_name && <p className="text-[11px] text-gray-400 mt-0.5">🏢 {agent.client_name}</p>}
        </div>
      </div>

      <div className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${availability.cls}`}>
        <span aria-hidden="true" className={`inline-block w-2 h-2 rounded-full ${availability.dotCls}`} />
        {availability.text}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${tier.cls}`} title="רמת הרשאה">
          {tier.text}
        </span>
        <span className="text-xs font-medium text-gray-700">{role}</span>
        {autonomy && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
            {autonomy}
          </span>
        )}
      </div>

      <div>
        <p className="text-[11px] font-medium text-gray-400 mb-1">יכולות</p>
        {unrestricted ? (
          <span
            className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100"
            title="allowed_tools ריק או לא מוגדר = כל הכלים המותרים ברמת ההרשאה הזו"
          >
            כל הכלים ברמת ההרשאה
          </span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {tools.slice(0, MAX_TOOL_CHIPS).map((tool) => (
              <span
                key={tool}
                dir="ltr"
                className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200"
              >
                {tool}
              </span>
            ))}
            {tools.length > MAX_TOOL_CHIPS && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 border border-gray-200">
                +{tools.length - MAX_TOOL_CHIPS}
              </span>
            )}
          </div>
        )}
      </div>

      {agent.requires_device_selection && (
        <p className="text-[11px] text-amber-600">⚠ נדרשת בחירת מכשיר לפני כל הרצה</p>
      )}

      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-gray-600 border-t pt-3 mt-auto">
        <div className="truncate" title={agent.model}>
          <span className="text-gray-400">מודל: </span>
          <span dir="ltr" className="font-mono text-[11px]">{agent.model}</span>
        </div>
        <div className="text-left">
          <span className="text-gray-400">הרצה אחרונה: </span>
          {agent.last_run_at ? (
            <Link
              to={`/agent-runs?agent_id=${agent.id}`}
              className="hover:underline"
              aria-label={`הרצות של ${agent.name}`}
            >
              {formatDateTime(agent.last_run_at)}
            </Link>
          ) : (
            formatDateTime(agent.last_run_at)
          )}
        </div>
        <div>
          {pendingCount > 0 ? (
            <Link
              to={`/agent-runs?agent_id=${agent.id}&status=pending_review`}
              className="rounded-full px-2 py-0.5 text-[11px] bg-orange-100 text-orange-700 hover:bg-orange-200"
              aria-label={`הרצות ממתינות של ${agent.name}`}
            >
              {pendingCount} ממתינים לאישור
            </Link>
          ) : (
            <span className="text-gray-400 text-[11px]">אין ממתינים לאישור</span>
          )}
        </div>
        <div className="text-left">
          {score != null ? (
            <Link
              to="/skills"
              className="text-[11px] font-semibold px-2 py-0.5 rounded-full border hover:opacity-80"
              style={{ color: scoreColor(score), borderColor: "currentColor" }}
            >
              ציון: {Math.round(score)}
            </Link>
          ) : (
            <span className="text-gray-300 text-[11px]">ציון: —</span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t pt-2">
        <Link
          to={`/agents/${agent.id}`}
          aria-label={`פתח מרחב עבודה — ${agent.name}`}
          className="border border-brand-600 text-brand-600 hover:bg-brand-50 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
        >
          פתח מרחב עבודה
        </Link>
        {canManage ? (
          <button
            type="button"
            onClick={onToggle}
            disabled={toggling}
            role="switch"
            aria-checked={agent.is_enabled}
            aria-label={agent.is_enabled ? `השבת את ${agent.name}` : `הפעל את ${agent.name}`}
            className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} relative w-10 h-5 rounded-full transition-colors disabled:opacity-50 ${
              agent.is_enabled ? "bg-brand-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                agent.is_enabled ? "left-0.5" : "right-0.5"
              }`}
            />
          </button>
        ) : (
          <span
            role="status"
            aria-label={agent.is_enabled ? "פעיל" : "לא פעיל"}
            title={agent.is_enabled ? "פעיל" : "לא פעיל"}
            className={`inline-block w-2.5 h-2.5 rounded-full ${agent.is_enabled ? "bg-green-500" : "bg-gray-300"}`}
          />
        )}
      </div>
    </article>
  );
}
